import React, { useState, useEffect, CSSProperties, useCallback } from 'react';
// @ts-ignore
import countryCodeOptions from './countryCodes.json';
import { CountryISOCode, CountryOption } from './types';
import { AUTOCOMPLETE_PREVENTION_PREFIX } from '../../../themes/venchi-v2/utils/constants';

interface PhoneCountryCodeInputProps {
    channelCountryRegionISOCode: string; // Default country ISO code (IT, DE, US, ...)
    isCountryCodeHidden?: boolean; // Config for disabling the country code selector
    phoneInputId: string; // For labels, and other html features
    phoneValue?: string; // Initial phone value (full number with country code)
    initialCountryDialCode?: string; // Optional dial code to preselect (e.g. '+39')
    defaultCountryCode?: CountryISOCode; // Optional custom country ISO code to preselect
    useDefaultCountryCode?: boolean; // Enable the defaultCountryCode logic (default: false)
    onPhoneNumberChange: (phoneNumber: string, fullPhoneNumber: string, hasValidationError: boolean) => void;
    resources: {
        phoneValidationErrorMin?: string;
        phoneValidationErrorMax?: string;
        phoneValidationErrorNumeric?: string;
        phoneValidationErrorRequired?: string;
    };
    className?: string;
    style?: CSSProperties;
    onBlur?: () => void;
    onFocus?: () => void;
    disableAutocomplete?: boolean; // Config for disabling browser and password manager autocomplete
}

/**
 * PhoneCountryCodeInput Component
 *
 * A reusable phone number input component with country code selector.
 * Supports international phone number formatting and validation.
 *
 * @component
 * @example
 * ```tsx
 * <PhoneCountryCodeInput
 *   channelCountryRegionISOCode="IT"
 *   phoneInputId="user_phone"
 *   phoneValue="+393331234567"
 *   defaultCountryCode="FR"
 *   useDefaultCountryCode={true} // Must be true to enable defaultCountryCode
 *   onPhoneNumberChange={(phone, fullPhone, hasError) => console.log(fullPhone)}
 *   resources={{
 *     phoneValidationErrorMin: "Minimum 6 digits",
 *     phoneValidationErrorMax: "Maximum 11 digits"
 *   }}
 * />
 * ```
 *
 * @param {CountryISOCode} [defaultCountryCode] - Optional 2-letter ISO country code to preselect.
 *   Must be used in conjunction with useDefaultCountryCode=true. Must be a valid code from countryCodes.json.
 * @param {boolean} [useDefaultCountryCode=false] - Enable the defaultCountryCode logic.
 *   When false (default), defaultCountryCode is ignored and component uses channelCountryRegionISOCode.
 *   When true, defaultCountryCode takes priority over channelCountryRegionISOCode.
 */
const PhoneCountryCodeInput: React.FC<PhoneCountryCodeInputProps> = ({
    channelCountryRegionISOCode,
    isCountryCodeHidden = false,
    phoneInputId,
    phoneValue,
    initialCountryDialCode,
    defaultCountryCode: customCountryCode,
    useDefaultCountryCode = false,
    onPhoneNumberChange,
    resources,
    className = '',
    style = {},
    onBlur,
    onFocus,
    disableAutocomplete = false
}) => {
    // Initialize selected country with priority: customCountryCode (if enabled) > initialCountryDialCode > channelCountryRegionISOCode > first in array
    const initialSelectedCountry = React.useMemo(() => {
        // If useDefaultCountryCode is enabled and a custom country code is provided, use it
        if (useDefaultCountryCode && customCountryCode) {
            const found = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.code === customCountryCode);
            if (found) {
                return found;
            }
        }

        // If an explicit dial code is provided, use it
        if (initialCountryDialCode) {
            const found = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.dial_code === initialCountryDialCode);
            if (found) {
                return found;
            }
        }

        // Otherwise try to use the channel country code
        const channelCountry = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.code === channelCountryRegionISOCode);
        if (channelCountry) {
            return channelCountry;
        }

        // Fallback to first country in the array
        return (countryCodeOptions as CountryOption[])[0];
    }, [useDefaultCountryCode, customCountryCode, initialCountryDialCode, channelCountryRegionISOCode]);

    const [selectedCountry, setSelectedCountry] = useState(initialSelectedCountry);
    const [phoneNumber, setPhoneNumber] = useState('');

    // Keep rendering order alphabetical by display name while preserving original array for logic
    const sortedCountryCodeOptions = React.useMemo(() => {
        return [...(countryCodeOptions as CountryOption[])].sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    const customStyles: { [key: string]: CSSProperties } = {
        container: {
            display: 'flex',
            flexDirection: 'row',
            padding: 0,
            width: '100%',
            ...style
        },
        dropdown: {
            width: '30%',
            height: '47px',
            fontSize: '13px',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0
        },
        input: {
            width: '70%',
            height: '47px',
            fontSize: '14px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0
        },
        option: {
            fontSize: '14px'
        }
    };

    // Initialize phone number from initial value if provided
    useEffect(() => {
        if (phoneValue) {
            // If useDefaultCountryCode is enabled and customCountryCode is set, use that and strip any prefix from phoneValue
            if (useDefaultCountryCode && customCountryCode) {
                const customCountry = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.code === customCountryCode);
                if (customCountry) {
                    setSelectedCountry(customCountry);
                    // Remove any existing country code prefix from phoneValue
                    const sortedCountries = [...(countryCodeOptions as CountryOption[])].sort(
                        (a: CountryOption, b: CountryOption) => b.dial_code.length - a.dial_code.length
                    );
                    const valueSanitized = phoneValue.replace(/\s+/g, '');
                    let phoneWithoutPrefix = phoneValue.replace(/\s+/g, '');
                    for (const country of sortedCountries) {
                        const countryDialSanitized = country.dial_code.replace(/\s+/g, '');
                        if (countryDialSanitized && valueSanitized.startsWith(countryDialSanitized)) {
                            phoneWithoutPrefix = valueSanitized.replace(countryDialSanitized, '');
                            break;
                        }
                    }
                    setPhoneNumber(phoneWithoutPrefix);
                    const fullPhoneNumber = `${sanitizeDialCode(customCountry.dial_code)}${phoneWithoutPrefix}`;
                    const validationResult = validatePhoneNumber(phoneWithoutPrefix);
                    onPhoneNumberChange(phoneWithoutPrefix, fullPhoneNumber, !!validationResult);
                    return;
                }
            }

            const detectCountryCode = (value: string) => {
                // Sort by dial code length (longest first) to avoid partial matches
                const sortedCountries = [...(countryCodeOptions as CountryOption[])].sort(
                    (a: CountryOption, b: CountryOption) => b.dial_code.length - a.dial_code.length
                );

                const valueSanitized = value.replace(/\s+/g, '');

                for (const country of sortedCountries) {
                    const countryDialSanitized = country.dial_code.replace(/\s+/g, '');
                    if (countryDialSanitized && valueSanitized.startsWith(countryDialSanitized)) {
                        return country;
                    }
                }
                return null;
            };

            const detectedCountry = detectCountryCode(phoneValue);
            if (detectedCountry) {
                setSelectedCountry(detectedCountry);
                const countryDialSanitized = detectedCountry.dial_code.replace(/\s+/g, '');
                const phoneWithoutPrefix = phoneValue.replace(countryDialSanitized, '').replace(/\s+/g, '');
                setPhoneNumber(phoneWithoutPrefix);
                // notify parent of initial parsed values
                const fullPhoneNumber = `${sanitizeDialCode(detectedCountry.dial_code)}${phoneWithoutPrefix}`;
                const validationResult = validatePhoneNumber(phoneWithoutPrefix);
                onPhoneNumberChange(phoneWithoutPrefix, fullPhoneNumber, !!validationResult);
            } else if (initialCountryDialCode) {
                // If no country detected in the phoneValue but an initial dial code was provided, prefer that
                const explicit = (countryCodeOptions as CountryOption[]).find(c => c.dial_code === initialCountryDialCode);
                if (explicit) {
                    setSelectedCountry(explicit);
                    const dialSanitized = explicit.dial_code.replace(/\s+/g, '');
                    const phoneWithoutPrefix = phoneValue.replace(dialSanitized, '').replace(/\s+/g, '');
                    setPhoneNumber(phoneWithoutPrefix);
                    const fullPhoneNumber = `${sanitizeDialCode(explicit.dial_code)}${phoneWithoutPrefix}`;
                    const validationResult = validatePhoneNumber(phoneWithoutPrefix);
                    onPhoneNumberChange(phoneWithoutPrefix, fullPhoneNumber, !!validationResult);
                } else {
                    setPhoneNumber(phoneValue.replace(/\s+/g, ''));
                }
            } else {
                setPhoneNumber(phoneValue.replace(/\s+/g, ''));
            }
        }
    }, [phoneValue, useDefaultCountryCode, customCountryCode]);

    const sanitizeDialCode = (dial: string): string => {
        if (!dial) {
            return '';
        }
        return dial.replace(/\s+/g, '');
    };

    const validatePhoneNumber = useCallback(
        (newPhoneNumber: string): string | false => {
            // Phone validation: 6-11 digits only
            const phoneRegex = /^\d{6,11}$/;

            if (newPhoneNumber.length === 0) {
                return resources.phoneValidationErrorRequired || 'Phone number is required';
            }

            if (!phoneRegex.test(newPhoneNumber)) {
                if (newPhoneNumber.length < 6) {
                    return resources.phoneValidationErrorMin || 'Phone number must be at least 6 digits';
                } else if (newPhoneNumber.length > 11) {
                    return resources.phoneValidationErrorMax || 'Phone number must be at most 15 digits';
                } else {
                    return resources.phoneValidationErrorNumeric || 'Phone number can only contain numbers';
                }
            }

            return false;
        },
        [resources]
    );

    const handlePhoneNumberChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const newPhoneNumber = event.target.value.replace(/[^\d]/g, ''); // Only allow digits
            setPhoneNumber(newPhoneNumber);

            const fullPhoneNumber = `${sanitizeDialCode(selectedCountry.dial_code)}${newPhoneNumber}`;
            const validationResult = validatePhoneNumber(newPhoneNumber);

            onPhoneNumberChange(newPhoneNumber, fullPhoneNumber, !!validationResult);
        },
        [selectedCountry.dial_code, validatePhoneNumber, onPhoneNumberChange]
    );

    const handleCountryCodeChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            const newCountryCode = event.target.value;
            const newSelectedCountry = (countryCodeOptions as CountryOption[]).find((c: CountryOption) => c.code === newCountryCode);

            if (newSelectedCountry) {
                setSelectedCountry(newSelectedCountry);
            }

            const fullPhoneNumber = `${sanitizeDialCode(newSelectedCountry?.dial_code || '')}${phoneNumber}`;
            const validationResult = validatePhoneNumber(phoneNumber);

            onPhoneNumberChange(phoneNumber, fullPhoneNumber, !!validationResult);
        },
        [phoneNumber, validatePhoneNumber, onPhoneNumberChange]
    );

    const handleBlur = useCallback(() => {
        onBlur?.();
    }, [onBlur]);

    const handleFocus = useCallback(() => {
        onFocus?.();
    }, [onFocus]);

    return (
        <>
            <div style={customStyles.container} className={className}>
                <select
                    style={customStyles.dropdown}
                    value={selectedCountry.code}
                    disabled={isCountryCodeHidden}
                    onChange={handleCountryCodeChange}
                    className='msc-address-form__phone-country-selector'
                    name='phone-country-code'
                    aria-label='Country code selector'
                    {...(disableAutocomplete && {
                        autoComplete: `${AUTOCOMPLETE_PREVENTION_PREFIX}-phone-country`,
                        'data-1p-ignore': true,
                        'data-lpignore': 'true',
                        'data-form-type': 'other',
                        'data-bwignore': true
                    })}
                >
                    {(sortedCountryCodeOptions as CountryOption[]).map((option: CountryOption, index: number) => (
                        <option key={index} value={option.code} style={customStyles.option}>
                            {option.name || option.code} {option.dial_code}
                        </option>
                    ))}
                </select>

                <input
                    type='tel'
                    id={phoneInputId}
                    name={phoneInputId}
                    style={customStyles.input}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    className='msc-address-form__phone-input'
                    minLength={6}
                    maxLength={11}
                    pattern='[0-9]*'
                    inputMode='numeric'
                    aria-label='Phone number'
                    {...(disableAutocomplete && {
                        autoComplete: `${AUTOCOMPLETE_PREVENTION_PREFIX}-phone-number`,
                        'data-1p-ignore': true,
                        'data-lpignore': 'true',
                        'data-form-type': 'other',
                        'data-bwignore': true,
                        autocapitalize: 'off',
                        autocorrect: 'off',
                        spellcheck: false
                    })}
                />

                {/* Hidden field to store the full phone number with country prefix */}
                <input
                    id={`${phoneInputId}_full`}
                    type='hidden'
                    name={`${phoneInputId}_full`}
                    value={`${sanitizeDialCode(selectedCountry.dial_code)}${phoneNumber}`}
                    {...(disableAutocomplete && {
                        autoComplete: `${AUTOCOMPLETE_PREVENTION_PREFIX}-phone-full`,
                        'data-1p-ignore': true,
                        'data-lpignore': 'true',
                        'data-form-type': 'other',
                        'data-bwignore': true
                    })}
                />
            </div>
        </>
    );
};

export default PhoneCountryCodeInput;
